import React from 'react'
import { Well } from '@pushly/aqe/lib/components'
import { DetailsSummaryField, DetailsSummarySection } from '@pushly/aqe/lib/components/details-summary-well'
import { VERTICAL_WELL_GAP } from '@pushly/aqe'
import { Alert, Button, Dropdown, Flex, Skeleton, Space, Tag, Tooltip } from 'antd'
import { EditOutlined, EllipsisOutlined, ExportOutlined, ReloadOutlined } from '@ant-design/icons'
import { formatArticleAgeText, isFeedSelected } from './helpers'
import type { FeedSelectionState } from './use-feed-selection'

// Load the shared feed styles here so the Feed Configuration card is styled
// wherever this tab mounts — including the unified content-source editor, whose
// root id would otherwise scope them out.
import './feed-setup-tab.scss'

/**
 * Renders a feed's keyword constraint list (required or restricted) as antd
 * tags for the read-only Article Constraints panel. Shows the actual keyword
 * values — these lists are typically short, so no truncation/"+N more" summary.
 *
 * Keys are composite (`keyword-index`) rather than the keyword alone: the feed
 * config is not guaranteed to be de-duplicated, and a repeated keyword would
 * otherwise produce duplicate React keys. Every configured entry is rendered as
 * stored — this panel reflects the feed, it does not normalize it.
 *
 * When the list is empty or undefined, renders a muted "None" placeholder.
 */
const renderKeywordTags = (keywords?: string[]): React.ReactNode => {
    if (!keywords?.length) {
        return <span className="feed-config-none">None</span>
    }

    return (
        <Flex wrap gap={4}>
            {keywords.map((keyword, index) => (
                <Tag key={`${keyword}-${index}`}>{keyword}</Tag>
            ))}
        </Flex>
    )
}

/**
 * True when a feed's source URL is safe to render as a live `href`.
 *
 * The feed URL is externally-supplied data, so only `http:`/`https:` are allowed
 * through — any other scheme (`javascript:`, `data:`, …) is rendered as plain
 * text instead. Unparseable values fail closed for the same reason.
 */
const isLinkableUrl = (url: string | undefined | null): boolean => {
    if (!url) return false

    try {
        const { protocol } = new URL(url)
        return protocol === 'http:' || protocol === 'https:'
    } catch {
        return false
    }
}

type FeedSetupTabProps = {
    /** Active domain id — used to build the feed View/Edit links. */
    domainId: number
    /**
     * View-only mode. Gates the EDIT affordance only: the Feed Configuration card
     * and its "View" action stay available, since inspecting the feed a step reads
     * from is part of reviewing the step.
     */
    readonly: boolean
    /** Feed selection state (from {@link useFeedSelection}). */
    feed: FeedSelectionState
}

/**
 * The Feed Setup tab content: a read-only Feed Configuration card for the
 * selected feed and the empty state shown until a feed is chosen. The feed
 * picker itself lives in the unified editor's "Source" well (above the tabs).
 *
 * The feed data + loaders are owned by {@link useFeedSelection} and passed in,
 * so the parent editor can read the current selection (and loaded feed) when it
 * composes the step on submit.
 */
export const FeedSetupTab: React.FC<FeedSetupTabProps> = ({ domainId, readonly, feed }) => {
    const { selectedFeedId, loadedFeed, loadingFeed, feedDetailError, refreshLoadedFeed } = feed

    const viewFeedHref = isFeedSelected(selectedFeedId) ? `/domains/${domainId}/feeds/${selectedFeedId}` : undefined

    const editFeedHref = isFeedSelected(selectedFeedId)
        ? `/domains/${domainId}/feeds/${selectedFeedId}/edit`
        : undefined

    // Navigate in a new tab so the journey editor drawer state is preserved. No-op
    // safe: only opens when the href resolved (a feed is selected).
    const openInNewTab = (href: string | undefined) => {
        if (href) {
            window.open(href, '_blank', 'noopener,noreferrer')
        }
    }

    return (
        <Flex vertical gap={VERTICAL_WELL_GAP} className="feed-setup-tab">
            {isFeedSelected(selectedFeedId) ? (
                feedDetailError ? (
                    <Alert
                        type="error"
                        showIcon
                        className="feed-config-error"
                        message="Unable to load this feed's configuration."
                    />
                ) : (
                    <Well
                        className="feed-configuration-card"
                        variant="default"
                        footerMode="hidden"
                        titleSize="sm"
                        title={
                            <span className="feed-config-header">
                                <span className="eyebrow-title">FEED CONFIGURATION</span>
                            </span>
                        }
                        actions={
                            <>
                                {/* In view-only mode the Edit menu item is not offered at
                                    all — a plain View button rather than a split View/Edit button
                                    with an empty menu. Viewing stays enabled either way. */}
                                {readonly ? (
                                    <Button
                                        size="small"
                                        className="feed-config-view-btn"
                                        onClick={() => openInNewTab(viewFeedHref)}
                                    >
                                        View
                                    </Button>
                                ) : (
                                    <Space.Compact className="feed-config-view-btn">
                                        <Button size="small" onClick={() => openInNewTab(viewFeedHref)}>
                                            View
                                        </Button>
                                        <Dropdown
                                            menu={{
                                                items: [
                                                    {
                                                        key: 'edit',
                                                        icon: <EditOutlined aria-hidden="true" />,
                                                        label: 'Edit',
                                                    },
                                                ],
                                                onClick: () => openInNewTab(editFeedHref),
                                            }}
                                        >
                                            <Button
                                                size="small"
                                                aria-label="More feed actions"
                                                icon={<EllipsisOutlined aria-hidden="true" />}
                                            />
                                        </Dropdown>
                                    </Space.Compact>
                                )}
                                {/* Refresh reloads the feed detail in place — the feed can be
                                    edited in the tab opened by View/Edit, so the loaded config
                                    can go stale. Sits to the right of View/Edit in both modes. */}
                                <Tooltip title="Refresh configuration">
                                    <Button
                                        type="text"
                                        size="small"
                                        aria-label="Refresh feed configuration"
                                        icon={<ReloadOutlined aria-hidden="true" />}
                                        loading={loadingFeed}
                                        onClick={refreshLoadedFeed}
                                    />
                                </Tooltip>
                            </>
                        }
                    >
                        {loadingFeed || !loadedFeed ? (
                            <Skeleton active loading paragraph={{ rows: 3 }} />
                        ) : (
                            <Flex vertical gap={12} className="feed-config-body">
                                <div className="feed-config-row">
                                    <span className="feed-config-label">Source URL</span>
                                    {isLinkableUrl(loadedFeed.url) ? (
                                        <a
                                            className="feed-source-url"
                                            href={loadedFeed.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            {loadedFeed.url}
                                            <ExportOutlined aria-hidden="true" />
                                        </a>
                                    ) : (
                                        <span className="feed-source-url">{loadedFeed.url}</span>
                                    )}
                                </div>

                                <DetailsSummarySection title="Article Constraints">
                                    <DetailsSummaryField
                                        label="Article age"
                                        value={formatArticleAgeText(loadedFeed.config?.articleMaxAge)}
                                    />
                                    <DetailsSummaryField
                                        label="Required keywords"
                                        value={renderKeywordTags(loadedFeed.config?.requiredKeywords)}
                                    />
                                    <DetailsSummaryField
                                        label="Restricted keywords"
                                        value={renderKeywordTags(loadedFeed.config?.restrictedKeywords)}
                                    />
                                </DetailsSummarySection>
                            </Flex>
                        )}
                    </Well>
                )
            ) : (
                <span className="feed-setup-empty-state">
                    Select a feed from the list above to view its configuration and continue to notification setup.
                </span>
            )}
        </Flex>
    )
}
